import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import type { ContactPointDto } from '../outreachTypes';
import { pointSourceLabel } from '../outreachLabels';
import { shortDate } from '../outreachFormat';
import { outreachMessages as messages } from '../outreachMessages';
import { findMessages } from '../find/findMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ContactPointsProps {
  accountId: number;
  /** The directory contact. Null for a private import. */
  contactUuid: string | null;
  points: ContactPointDto[];
}

function iconOf(type: string): IconDefinition {
  if (type === 'email') {
    return faEnvelope;
  }
  return type === 'instagram' || type === 'tiktok' ? faUser : faArrowUpRightFromSquare;
}

/**
 * The contact points with their mandatory source line (design outreach c2, q2): an email shows
 * "Copy", other points open their public page. Music24 never messages on Instagram or TikTok.
 * Outreach v2 has no reveal: a contact got a pitch, so its address is plain. A masked point comes
 * only from Find, for a person that Plug did not pitch yet; it has no "Copy".
 */
const ContactPointsComponent: React.FC<ContactPointsProps> = ({ points }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState<string | null>(null);

  const copy = useCallback((value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => setCopied(value))
      .catch(console.error);
  }, []);

  const rows = useMemo(
    () =>
      points.map((point) => {
        const source = intl.formatMessage(messages.sourceLine, {
          source: intl.formatMessage(pointSourceLabel(point.source)),
          date: shortDate(intl, point.foundAt),
        });
        const isEmail = point.type === 'email';
        return (
          <div key={point.pointUuid} className={styles.pointRow} data-testid={`contact-point-${point.type}`}>
            <span className={clsx(styles.contactIcon, point.bounced && styles.contactIconOff)}>
              <FontAwesomeIcon icon={iconOf(point.type)} />
            </span>
            <div className={styles.grow}>
              <b className={clsx(point.masked && styles.blur)}>{point.value}</b>
              <small>{withBrandWord(point.bounced ? intl.formatMessage(findMessages.bounced) : source)}</small>
              {point.invitationText !== null && <small>“{point.invitationText}”</small>}
            </div>
            {isEmail && !point.masked && (
              <SMLButton size="sm" outline omitCap leftIcon={faCopy} onClick={() => copy(point.value)}>
                {intl.formatMessage(copied === point.value ? messages.copied : messages.copy)}
              </SMLButton>
            )}
            {!isEmail && (
              <a
                className="btn btn-sm btn-link"
                href={point.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={intl.formatMessage(messages.openLink)}
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </a>
            )}
          </div>
        );
      }),
    [copied, copy, intl, points]
  );

  return <div className="d-flex flex-column gap-2">{rows}</div>;
};

export const ContactPoints = TypedMemo(ContactPointsComponent);
